import React, {useId} from 'react';
import {IonIcon, IonSpinner} from '@ionic/react';
import {alertCircleOutline, checkmarkCircleOutline, radioOutline} from 'ionicons/icons';
import {NfcCommonProps} from './types';
import {NfcOperationState} from './useNfcOperation';
import {getNfcUnavailableMessage} from './webNfc';
import './NfcCard.css';

interface NfcCardProps extends Omit<NfcCommonProps, 'onError'> {
    title: string;
    description: string;
    state: NfcOperationState;
    actionLabel: string;
    retryLabel?: string;
    waitingMessage: string;
    successMessage: string;
    validationMessage?: string;
    onStart: () => void;
    onCancel: () => void;
    children?: React.ReactNode;
}

const getFeedback = ({state, actionLabel, retryLabel, waitingMessage, successMessage, validationMessage}: NfcCardProps) => {
    const unavailable = getNfcUnavailableMessage();
    const blockedMessage = unavailable || validationMessage;
    const messages = {idle: 'Klaar om te starten.', waiting: waitingMessage, success: successMessage, error: state.message};
    const icons = {
        idle: radioOutline, waiting: radioOutline, success: checkmarkCircleOutline, error: alertCircleOutline,
    };
    return {
        phase: unavailable ? 'unavailable' : state.phase,
        message: blockedMessage || messages[state.phase],
        icon: unavailable ? alertCircleOutline : icons[state.phase],
        blocked: Boolean(blockedMessage),
        label: state.phase === 'error' ? retryLabel ?? 'Opnieuw proberen' : actionLabel,
    };
};

const NfcCard: React.FC<NfcCardProps> = (props) => {
    const {title, description, state, disabled, className, onStart, onCancel, children} = props;
    const id = useId();
    const {phase, message, icon, blocked, label} = getFeedback(props);
    const waiting = state.phase === 'waiting';

    return (
        <section
            className={`nfc-card nfc-card--${phase} ${className || ''}`}
            aria-labelledby={`${id}-title`}
        >
            <div className="nfc-card__header">
                <span className="nfc-card__icon" aria-hidden="true">
                    <IonIcon icon={icon}/>
                </span>
                <div>
                    <h3 id={`${id}-title`}>{title}</h3>
                    <p className="nfc-card__description">{description}</p>
                </div>
            </div>
            {children}
            <p
                className="nfc-card__status"
                id={`${id}-status`}
                role={state.phase === 'error' ? 'alert' : 'status'}
                aria-atomic="true"
            >
                {waiting && <IonSpinner name="crescent" aria-hidden="true"/>}
                <span>{message}</span>
            </p>
            <div className="nfc-card__actions">
                <button
                    type="button"
                    className="nfc-card__primary"
                    disabled={disabled || waiting || blocked}
                    onClick={onStart}
                    aria-describedby={`${id}-status`}
                >
                    {waiting ? 'Wachten op tag…' : label}
                </button>
                {waiting && (
                    <button type="button" className="nfc-card__cancel" onClick={onCancel}>
                        Annuleren
                    </button>
                )}
            </div>
        </section>
    );
};

export default NfcCard;
